import { supabase } from '../supabaseClient';

/**
 * Check if the current user can make more comparisons this week
 * @param {string} userId - The user ID to check
 * @returns {Promise<{canCompare: boolean, comparisonsUsed: number, comparisonsRemaining: number}>}
 */
export const checkUserComparisonLimit = async (userId) => {
  if (!userId) {
    return { canCompare: true, comparisonsUsed: 0, comparisonsRemaining: 3 };
  }

  try {
    const { data, error } = await supabase.rpc('check_user_comparison_limit', {
      user_id: userId,
      weekly_limit: 3
    });

    if (error) {
      console.error('Error checking user comparison limit:', error);
      // Default to allowing comparisons if there's an error
      return { canCompare: true, comparisonsUsed: 0, comparisonsRemaining: 3 };
    }

    if (data && data.length > 0) {
      const result = data[0];
      return {
        canCompare: result.can_compare,
        comparisonsUsed: result.comparisons_used,
        comparisonsRemaining: result.comparisons_remaining
      };
    }

    // Default response if no data
    return { canCompare: true, comparisonsUsed: 0, comparisonsRemaining: 3 };
  } catch (err) {
    console.error('Error in checkUserComparisonLimit:', err);
    return { canCompare: true, comparisonsUsed: 0, comparisonsRemaining: 3 };
  }
};

/**
 * Increment the comparison count for the current user
 * @param {string} userId - The user ID to increment
 * @returns {Promise<{success: boolean, newCount: number, message: string}>}
 */
export const incrementUserComparisonCount = async (userId) => {
  if (!userId) {
    return { success: true, newCount: 0, message: 'No user ID provided' };
  }

  try {
    const { data, error } = await supabase.rpc('increment_user_comparison_count', {
      user_id: userId
    });

    if (error) {
      console.error('Error incrementing user comparison count:', error);
      return { success: false, newCount: 0, message: 'Error tracking comparison' };
    }

    if (data && data.length > 0) {
      const result = data[0];
      return {
        success: result.success,
        newCount: result.new_count,
        message: result.message
      };
    }

    return { success: false, newCount: 0, message: 'No data returned' };
  } catch (err) {
    console.error('Error in incrementUserComparisonCount:', err);
    return { success: false, newCount: 0, message: 'Error tracking comparison' };
  }
};

/**
 * Get comparison status for display purposes
 * @param {string} userId - The user ID to check
 * @returns {Promise<string>} A formatted string showing comparison status
 */
export const getUserComparisonStatus = async (userId) => {
  const status = await checkUserComparisonLimit(userId);
  
  if (status.comparisonsRemaining === 0) {
    return 'You have reached your weekly comparison limit (3/3 used). Resets Monday.';
  } else if (status.comparisonsUsed > 0) {
    return `${status.comparisonsUsed}/3 weekly comparisons used. ${status.comparisonsRemaining} remaining.`;
  } else {
    return '3 weekly comparisons available.';
  }
};

/**
 * Track a profile comparison (call this when a user successfully votes)
 * @param {string} userId - The user's ID
 * @param {string} comparisonId - A unique identifier for this comparison
 * @returns {Promise<boolean>} Whether the tracking was successful
 */
export const trackUserComparison = async (userId, comparisonId) => {
  try {
    const result = await incrementUserComparisonCount(userId);
    
    if (result.success) {
      console.log(`Comparison tracked for user ${userId}. Count: ${result.newCount}/3`);
      return true;
    } else {
      console.warn('Failed to track comparison:', result.message);
      return false;
    }
  } catch (error) {
    console.error('Error tracking comparison:', error);
    return false;
  }
}; 